//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { CuralateCatalogProduct } from '@apis/universal-image-actions/universalImageActionsApiModel';
import {
  InvisibleButton,
  PrimaryButton,
} from '@components/atoms/button/Button';
import { SectionText } from '@components/atoms/text/Text';
import { TaggedProducts } from '@uia/products/TaggedProducts';
import { UiaTagProducts } from '@uia/products/UiaTagProducts';
import { UiaProductTrackingIds } from '@uia/products/constants/UiaProductConstants';
import { useProductTaggingContext } from '@uia/products/context/ProductTaggingContext';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import { pluralize } from '@utils/stringUtils';

export const ProductsContainer = () => {
  const {
    bulkTagProducts,
    cancelNewSpatialTag,
    isTaggingProducts,
    metadata,
    searchQuery,
    selectedProductToEdit,
    setIsTaggingProducts,
    setSearchQuery,
    setSelectedProductToEdit,
  } = useProductTaggingContext();

  const [selectedProducts, setSelectedProducts] = useState<
    CuralateCatalogProduct[]
  >([]);

  const isProductEditorEnabled = isDefined(selectedProductToEdit);

  const onClose = () => {
    setIsTaggingProducts(false);
    setSelectedProductToEdit(undefined);
    setSearchQuery('');
    cancelNewSpatialTag();
  };

  useEffect(() => {
    setSelectedProductToEdit(undefined);
  }, [searchQuery, setSelectedProductToEdit]);

  return (
    <>
      <Container>
        <TitleContainer>
          {isProductEditorEnabled && !isTaggingProducts ? (
            <SectionText>Edit Products Details</SectionText>
          ) : (
            <SectionText>Add Products</SectionText>
          )}
          {isTaggingProducts && (
            <InvisibleButton
              aria-label='Close'
              iconName='close'
              onClick={() => onClose()}
              trackingId={UiaProductTrackingIds.PRODUCT_SECTION_CLOSE_BUTTON}
              type='button'
            />
          )}
        </TitleContainer>
        {(isTaggingProducts || isProductEditorEnabled) && (
          <UiaTagProducts
            enableEditor={setSelectedProductToEdit}
            onClose={onClose}
            selectedProducts={selectedProducts}
            setSelectedProducts={setSelectedProducts}
          />
        )}
        {!isTaggingProducts && !isProductEditorEnabled && <TaggedProducts />}
      </Container>
      {isTaggingProducts && isUndefined(selectedProductToEdit) && (
        <BulkTagProducts>
          <PrimaryButton
            isDisabled={selectedProducts.length === 0}
            onClick={() => {
              bulkTagProducts(selectedProducts, metadata.productTags);
              setIsTaggingProducts(false);
              setSearchQuery('');
              setSelectedProducts([]);
            }}
            trackingId={UiaProductTrackingIds.BULK_TAG_PRODUCTS_BUTTON}
          >
            Tag&nbsp;
            {selectedProducts.length > 0
              ? pluralize(selectedProducts.length, 'Product')
              : `Products`}
          </PrimaryButton>
        </BulkTagProducts>
      )}
    </>
  );
};

const Container = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
    padding: ${theme.spacings[3]};
  `,
);

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const BulkTagProducts = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: end;
    position: sticky;
    bottom: 0;
    border-top: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
    padding: ${theme.spacings[3]};
    background-color: ${theme.colors.white};
  `,
);
